// ============================================================
// [2.13.0] Console d'un serveur Siverix — /sso-retour?assertion=…&issuer=…
// Retour du serveur de comptes après le consentement. L'assertion est lue puis
// aussitôt effacée de l'adresse (historique, copier-coller), présentée à
// POST /auth/sso/login (même session qu'une connexion classique : cookie de
// rafraîchissement + jeton d'accès), et la console s'ouvre.
// ============================================================

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '@siverix/api-client';
import { useAuth } from '../auth/AuthContext';

export function SsoReturnPage(): JSX.Element {
  const { loading, ssoLogin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Lu une seule fois, avant l'effacement de l'adresse.
  const params = useRef<{ assertion: string | null; issuer: string | null } | null>(null);
  if (params.current === null) {
    const q = new URLSearchParams(location.search);
    params.current = { assertion: q.get('assertion'), issuer: q.get('issuer') };
  }
  const started = useRef(false);
  const [err, setErr] = useState<{ message: string; hint?: string } | null>(null);

  // Efface tout de suite l'assertion de la barre d'adresse (avant affichage).
  useLayoutEffect(() => {
    window.history.replaceState(window.history.state, '', window.location.pathname);
  }, []);

  useEffect(() => {
    // Après la reprise de session au démarrage (AuthContext), jamais en même temps.
    if (loading || started.current) return;
    started.current = true;
    const { assertion, issuer } = params.current ?? { assertion: null, issuer: null };
    if (!assertion || !issuer) {
      setErr({ message: 'Ce lien de connexion est incomplet. Recommence depuis la connexion.' });
      return;
    }
    void (async () => {
      try {
        await ssoLogin(assertion, issuer);
        navigate('/', { replace: true });
      } catch (e: unknown) {
        const hint =
          e instanceof ApiError && e.code === 'InvalidAssertion'
            ? "Le lien a peut-être expiré (2 minutes). Si ça recommence, vérifie que l'adresse publique de ce serveur Siverix est bien celle de cette page."
            : undefined;
        setErr({ message: (e as Error).message || 'La connexion a échoué.', hint });
      }
    })();
  }, [loading, ssoLogin, navigate]);

  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 16 }}>
      <div
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 12,
          padding: 32,
          width: '100%',
          maxWidth: 440,
          textAlign: 'center',
        }}
      >
        {err ? (
          <>
            <h1 style={{ marginTop: 0, fontSize: 20 }}>Connexion impossible</h1>
            <p role="alert" style={{ color: 'var(--color-error)', fontSize: 14 }}>
              {err.message}
            </p>
            {err.hint && (
              <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{err.hint}</p>
            )}
            <Link to="/login">Revenir à la connexion</Link>
          </>
        ) : (
          <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
            Connexion avec ton compte Siverix…
          </p>
        )}
      </div>
    </div>
  );
}
